import { useCallback, useEffect, useState } from "react";
import { Button, Chip, TextField } from "@legends/ui";
import {
  createCaveFront,
  fetchCaveFronts,
  setCaveFrontArchived,
  type CaveFront,
} from "../api";
import { Section, alertStyle, cardGridStyle, cardStyle, errMsg, monoLabelStyle } from "../sections";

// Caves (fronts) management (CF-5, LSC-372): the public storefronts in front of a
// Cave Backend. Each Cave has its own host + branding + (front-scoped) events +
// ticket catalog; the default Cave (id == backend id) is the backend's own
// storefront and can't be archived. Admin-only (canWrite = catalogWrite).
export default function CaveFrontsTab({
  caveId,
  caveName,
  canWrite = false,
}: {
  caveId: string;
  caveName: string;
  canWrite?: boolean;
}) {
  const [fronts, setFronts] = useState<CaveFront[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [nuName, setNuName] = useState("");
  const [nuSlug, setNuSlug] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetchCaveFronts(caveId)
      .then(setFronts)
      .catch((e: unknown) => setError(errMsg(e)));
  }, [caveId]);
  useEffect(() => {
    setError(null);
    load();
  }, [load]);

  const create = () => {
    if (!nuName.trim()) {
      setError("A Cave needs a name.");
      return;
    }
    setError(null);
    setNotice(null);
    setBusy(true);
    createCaveFront(caveId, { name: nuName.trim(), ...(nuSlug.trim() ? { slug: nuSlug.trim() } : {}) })
      .then((res) => {
        setNotice(`Created “${res.front.name}” at ${res.front.host} (DNS ${res.dns}).`);
        setNuName("");
        setNuSlug("");
        load();
      })
      .catch((e: unknown) => setError(errMsg(e)))
      .finally(() => setBusy(false));
  };

  const toggleArchive = (f: CaveFront) => {
    setError(null);
    setCaveFrontArchived(caveId, f.id, !f.archived)
      .then(load)
      .catch((e: unknown) => setError(errMsg(e)));
  };

  return (
    <Section title="Caves">
      <p style={{ margin: "4px 0 12px", fontSize: "var(--fs-sm)", color: "var(--text-2, var(--text-dim))" }}>
        Public storefronts for <strong>{caveName}</strong> — each Cave has its own web address, branding,
        events and ticket catalog, all backed by this one operational Cave Backend.
      </p>
      <div style={cardGridStyle}>
        {fronts.map((f) => (
          <div key={f.id} style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi, var(--text-1))" }}>{f.name}</span>
              {f.default && <Chip color="var(--accent, #c9a227)">DEFAULT</Chip>}
              {f.archived && <Chip aria-label={`${f.name} archived`}>ARCHIVED</Chip>}
            </div>
            <div style={{ ...monoLabelStyle, marginTop: 4 }}>{f.host}</div>
            {canWrite && !f.default && (
              <div style={{ marginTop: 10 }}>
                <Button
                  size="sm"
                  variant="muted"
                  aria-label={`${f.archived ? "Unarchive" : "Archive"} ${f.name}`}
                  onClick={() => toggleArchive(f)}
                >
                  {f.archived ? "Unarchive" : "Archive"}
                </Button>
              </div>
            )}
          </div>
        ))}
        {fronts.length === 0 && !error && (
          <p style={{ margin: 0, gridColumn: "1 / -1", ...monoLabelStyle }}>No Caves.</p>
        )}
      </div>

      {canWrite && (
        <form
          aria-label="Add a Cave"
          onSubmit={(e) => {
            e.preventDefault();
            create();
          }}
          style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-subtle, var(--line-strong))", display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}
        >
          <TextField label="Cave name" name="front-name" value={nuName} onChange={setNuName} placeholder="e.g. Buffalo" />
          <TextField label="Web address (optional)" name="front-slug" value={nuSlug} onChange={setNuSlug} placeholder="buffalo" />
          <Button variant="accent" type="submit" disabled={busy || !nuName.trim()}>
            {busy ? "Creating…" : "Add a Cave"}
          </Button>
        </form>
      )}
      {notice && <p style={{ ...monoLabelStyle, margin: "10px 0 0", color: "var(--accent, #c9a227)" }}>{notice}</p>}
      {error && <p role="alert" style={alertStyle}>{error}</p>}
    </Section>
  );
}
